import React from 'react';
import { styled } from '@components-shared/src/stitches';
import { WebWorkerImage } from '@components/basics/Images/WebWorkerImage';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import blurImage from '@components/main-components/image/blur-image';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { MediaQuery } from '@context/mediaQuery';
import { webappUriPath } from '@utils/url';

const MOBILE_SIZE = 343;
const DESKTOP_SIZE = 303;

export const CoverImage = ({
  coverImage,
  releaseTitle,
  trackDisabled = false,
  hasCountdown = false,
  isAnimated = false,
  webappUri,
}: {
  coverImage: string;
  releaseTitle: string;
  trackDisabled?: boolean;
  hasCountdown?: boolean;
  isAnimated?: boolean;
  webappUri: string;
}) => {
  const { tablet, tabletLandscape } = MediaQuery.useContainer();

  const imageSize = tabletLandscape ? DESKTOP_SIZE : undefined;

  return (
    <Container>
      <AccessibleLink pathnameUri={webappUriPath(webappUri)}>
        {isAnimated ? (
          <WebWorkerImage src={coverImage} width={imageSize} height={imageSize} />
        ) : (
          <NextFutureImage
            alt={releaseTitle}
            src={coverImage}
            width={imageSize}
            height={imageSize}
            fill={!tabletLandscape}
            placeholder="blur"
            blurDataURL={blurImage(MOBILE_SIZE, MOBILE_SIZE)}
          />
        )}

        {hasCountdown && !tablet && (
          <TimerGradient
            css={{
              pointerEvents: 'none',
              width: '100%',
              height: '100%',
              div: {
                width: 'inherit',
                height: '45%',
              },
            }}
          >
            <Gradient color="black2" />
            <Gradient
              color="black2"
              css={{
                pointerEvents: 'none',
                bottom: '10.5%',
                transform: ' matrix(1, 0, 0, -1, 0, 0)',
              }}
            />
          </TimerGradient>
        )}

        {!tabletLandscape && hasCountdown ? (
          <Gradient
            color="black3"
            css={{
              pointerEvents: 'none',

              top: 0,
              transform: ' matrix(1, 0, 0, -1, 0, 0)',
            }}
          />
        ) : (
          !tabletLandscape &&
          hasCountdown && (
            <Gradient
              color="white"
              css={{
                pointerEvents: 'none',
              }}
            />
          )
        )}

        {!tabletLandscape && (
          <Gradient
            color="black"
            css={{
              pointerEvents: 'none',
              bottom: 0,
              height: trackDisabled ? '50%' : '100%',

              '@tablet': {
                height: '100%',
              },
            }}
          />
        )}
      </AccessibleLink>
    </Container>
  );
};

const Container = styled('div', {
  position: 'relative',
  aspectRatio: '1 / 1',
  height: 'fit-content',
  borderRadius: 8,
  overflow: 'hidden',
  width: '100%',
  '&:hover': {
    cursor: 'pointer',
  },

  '@tabletLandscape': {
    width: DESKTOP_SIZE,
    height: DESKTOP_SIZE,
  },
});

const Gradient = styled('div', {
  position: 'absolute',
  borderRadius: 8,
  width: '100%',
  height: '100%',
  variants: {
    color: {
      white: {
        background:
          'linear-gradient(135deg,rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0) 50%)',
      },
      black: {
        background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%,  rgba(0, 0, 0, 0.8)  100%)',
      },
      black2: {
        borderRadius: 0,
        background:
          'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 80%,rgba(0,0,0, 1) 100%)',
      },
      black3: {
        background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 30%,  rgba(0, 0, 0, 0.8)  100%)',
      },
    },
  },
});

const TimerGradient = styled('div', {
  position: 'absolute',
  top: 0,
  borderRadius: 8,
  width: '100%',
  height: '100%',
});
